import styled from "@emotion/styled";
import moreScreenMobileBackground from "../../../img/navigation/more-screen-mobile-background.svg";
import { getNavbarButtonOptions, linkItems } from "../NavbarV2Desktop";
import { BebopWhiteIcon, WavyClose } from "components/Icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { SideNavSocials } from "./SideNavSocials";
import { Link, useLocation } from "react-router-dom";
import { isInternalPath } from "utils/urlUtils";
import { Theme } from "styles/theme";
import { css } from "@emotion/react";
import { BEBOP_ROUTES, Z_INDICES } from "config/consts";
import { withIsVisible } from "utils/reactUtils";
import { GlobalStore } from "store/global/GlobalStore";
import { Button } from "components/general/Button/Button";
import { Text } from "components/general/Text";
import { MOBILE_NAVBAR_HEIGHT } from "../NavbarV2Mobile";

interface SideNavProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isMobileNavBarDisplayed?: boolean;
}

export const SideNav = ({ isOpen, setIsOpen, isMobileNavBarDisplayed = false }: SideNavProps) => {
  const [isInternalOpen, setIsInternalOpen] = useState(isOpen);
  const isHomePage = useLocation().pathname === BEBOP_ROUTES.Home;
  const theme = GlobalStore.useState(s => s.theme);

  useEffect(() => {
    if (!isOpen) {
      // Ensures that this component is displayed for another 300ms till the
      // sliding out animation finishes
      setTimeout(() => setIsInternalOpen(false), 300);
    } else {
      setIsInternalOpen(true);
    }
  }, [isOpen]);

  // Prevent mobile nav from flashing slide-out animation on first load
  const justMounted = useRef(true);
  useEffect(() => {
    if (!justMounted) return;
    justMounted.current = false;
  }, []);

  const className = useMemo(() => {
    if (justMounted.current) {
      return "";
    } else if (isOpen) {
      return "slide-in";
    }
    return "slide-out";
  }, [isOpen]);

  const ctaContent = useMemo(() => {
    if (isHomePage) {
      return (
        <BottomContainer>
          <SocialsContainerHomePage>
            <SideNavSocials />
          </SocialsContainerHomePage>
          {isHomePage && (
            <Button
              navigateTo={BEBOP_ROUTES.Trade}
              text="Start Trading"
              buttonCss={css`
                border-radius: 14px;
                height: 48px;
                justify-content: center;
              `}
            >
              <Text
                as={"span"}
                textStyle="Button"
                size="Large"
                color={theme.button.primary.text.default}
              >
                Start Trading
              </Text>
            </Button>
          )}
        </BottomContainer>
      );
    }
    return (
      <SocialsContainerDefault>
        <SideNavSocials />
      </SocialsContainerDefault>
    );
  }, [isHomePage, theme.button.primary.text.default]);

  return (
    <Container
      className={className}
      isOpen={isInternalOpen}
      isMobileNavBarDisplayed={isMobileNavBarDisplayed}
    >
      <BebopWhiteIcon width={118} height={40} />
      <CloseButton isVisible={isHomePage} onClick={() => setIsOpen(false)}>
        <WavyClose width={32} height={32} color={theme["Brand colours"].Sea} />
      </CloseButton>
      <LinkItemContainer>
        {linkItems.map(item => {
          const isInternal = isInternalPath(item.to);
          const LinkItem = isInternal ? InternalLink : ExternalLink;
          const target = isInternal ? "_self" : "_blank";
          return (
            <LinkItem
              key={item.text}
              to={item.to}
              href={item.to}
              target={target}
              onClick={() => setIsOpen(false)}
              referrerPolicy="no-referrer"
            >
              {item.text}
            </LinkItem>
          );
        })}
      </LinkItemContainer>
      {ctaContent}
    </Container>
  );
};

// Sub-components

interface ContainerProps {
  isOpen: boolean;
  isMobileNavBarDisplayed: boolean;
}

const Container = styled.div<ContainerProps>`
  display: ${props => (props.isOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  height: ${props =>
    props.isMobileNavBarDisplayed ? `calc(100% - ${MOBILE_NAVBAR_HEIGHT}px)` : "100dvh"};
  width: 100%;
  background-color: ${props => props.theme.surface.alt.primary.default};
  padding-left: max(18px, 8%);
  padding-right: max(18px, 8%);
  padding-top: max(56px, 14%);
  background-image: url(${moreScreenMobileBackground});
  background-repeat: no-repeat;
  background-size: 108vw;
  opacity: 0;
  z-index: ${Z_INDICES.NAV_BAR};
`;

const LinkItemContainer = styled.div`
  margin-top: 125px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
`;

const linkStyle = (theme: Theme) => css`
  color: ${theme.theme[1600]};
  font-weight: ${theme.fontWeights["work-sans-0"]};
  line-height: ${theme.lineHeights[3]};
`;

const InternalLink = styled(Link)`
  ${props => linkStyle(props.theme)}
`;

const ExternalLink = styled.a`
  ${props => linkStyle(props.theme)}
`;

const BottomContainer = styled.div`
  position: absolute;
  bottom: 20px;
  width: 84.5%;
`;

const SocialsContainerDefault = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => getNavbarButtonOptions(props.theme).length + 1}, 1fr);
  grid-template-rows: 1fr;
  margin-bottom: 16px;
  position: absolute;
  bottom: 0px;
  width: 100dvw;
  left: 0;

  div {
    margin: auto;
  }
`;

const SocialsContainerHomePage = styled(SocialsContainerDefault)`
  bottom: 0;
  width: 100%;
  position: relative;

  div {
    margin: unset;
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const CloseButton = withIsVisible(styled.button`
  position: absolute;
  top: 14px;
  right: 12px;
  width: 30px;
  height: 30px;
  border-radius: 44px;
  padding: 12px;
  background-color: ${props => props.theme.surface.main.primary.default};
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: ${props => props.theme.surface.main.primary.hover};
  }
`);
